import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  forkJoin,
  from,
  Observable,
  of,
  Subscription
} from "rxjs";
import { catchError, filter, map, switchMap, take } from "rxjs/operators";
import { Did, DidService } from "@onsip/common/services/api/resources/did/did.service";
import { OrganizationService } from "@onsip/common/services/api/resources/organization/organization.service";
import { User, UserService } from "@onsip/common/services/api/resources/user/user.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";

import { Role } from "@onsip/common/services/api/role";
import { FormControl, FormGroup, NonNullableFormBuilder } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { MatDialog } from "@angular/material/dialog";
import { TransferNumbersModalComponent } from "@onsip/web/features/accountSettings/transfer-numbers/transfer-numbers-modal/transfer-numbers-modal.component";
import { MatSort } from "@angular/material/sort";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { TranslateService } from "@ngx-translate/core";
import { FormatE164Pipe } from "../../shared/pipes/format-e164.pipe";
import { Form } from "../../shared/interfaces/form.interface";

interface TransferNumberForm {
  search: string;
  organizationsList: Array<Pbx> | Pbx;
  selectAll: boolean;
  selectedElements: Array<TransferredDid>;
}

interface TransferredDid extends Did {
  domain: string;
  pbx: string;
  control: FormControl;
}

export interface Pbx {
  organizationId: string;
  organizationName: string;
  domain: string;
}

@Component({
  selector: "onsip-transfer-numbers",
  templateUrl: "./transfer-numbers.component.html",
  styleUrls: ["./transfer-numbers.component.scss"],
  providers: [FormatE164Pipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TransferNumbersComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild(MatSort) set matSort(matSort: MatSort) {
    this.sort = matSort;
    this.dataSource.sort = matSort;
  }

  private unsubscriber = new Subscription();
  allPbxes!: Array<TransferredDid>;
  form!: FormGroup<Form<TransferNumberForm>>;
  dataSource = new MatTableDataSource<TransferredDid>();
  maxItemCount = 0;
  displayedColumns$ = new BehaviorSubject(["number", "pbx"]);
  organizationsFilterList!: Array<Pbx>;
  organizationsList!: Array<Pbx>;
  isDataObtained$ = new BehaviorSubject(false);

  accountId$ = this.organizationService.orgState.pipe(map(orgState => orgState[0].accountId));

  constructor(
    private didService: DidService,
    private organizationService: OrganizationService,
    private userService: UserService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    public snackbarService: SnackbarService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private formatE164: FormatE164Pipe,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.obtainData();
    this.getDataFromStore();
    this.onBreakpointChange();
    this.onSearch();
    this.onSelectAll();
    this.initSorting();
    this.setDataFilter();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  private initForm(): void {
    this.form = this.fb.group<Form<TransferNumberForm>>({
      search: this.fb.control(""),
      organizationsList: this.fb.control([]),
      selectAll: this.fb.control(false),
      selectedElements: this.fb.control([])
    });
  }

  private obtainData(): void {
    this.organizationService.organizationBrowse();
    this.unsubscriber.add(
      this.accountId$.pipe(take(1)).subscribe(accountId => {
        this.didService.didBrowse({ OrganizationId: undefined, AccountId: accountId });
      })
    );
  }

  onTransferPhone(
    pbx: Pbx,
    phone: TransferredDid,
    users: Array<User>
  ): Observable<ApiPromiseSubject<Did>> {
    const org = this.organizationsList.find(org => org.domain === pbx.domain);
    return of(org).pipe(
      switchMap(organization => {
        if (organization) {
          const newUser = this.getNewUser(organization, users);
          return this.didService.didAssign(phone.didId, `sip.jnctn.net`, {
            OrganizationId: organization?.organizationId,
            UserId: newUser?.userId
          });
        }
        return EMPTY;
      })
    );
  }

  private showSuccessDialog(pbx: Pbx): void {
    const successDialog = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      maxWidth: "292px",
      disableClose: true,
      autoFocus: false,
      data: {
        title: this.translate.instant("ONSIP_I18N.YOUR_NUMBERS_HAVE_TRANSFERRED"),
        message: this.translate.instant(
          "ONSIP_I18N.PLEASE_GO_TO_THE_PHONE_NUMBERS_PAGE_FOR_VALUE_TO_UPDATE_THE_DESTINATION_FOR_EACH_NUMBER",
          { value: pbx?.domain }
        ),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        primaryBtnColor: "primary",
        primaryBtnFlat: true,
        showOnlyPrimaryBtn: true
      }
    });

    this.unsubscriber.add(
      successDialog.afterClosed().subscribe(() => {
        this.form.controls.selectedElements.setValue([]);
        this.cdr.markForCheck();
      })
    );
  }

  private getNewUser(organization: Pbx, users: Array<User>): User {
    const newOrganizationUsers: Array<User> = users.filter(
      ({ organizationId }) => organizationId === organization?.organizationId
    );
    let newUser = newOrganizationUsers.find(({ roles }) =>
      roles.some(role => role === Role.AccountAdmin)
    );

    if (!newUser) {
      newUser = newOrganizationUsers.find(({ roles }) =>
        roles.some(role => role === Role.OrganizationAdmin)
      );
    }

    if (!newUser) {
      newUser = newOrganizationUsers[0];
    }

    return newUser;
  }

  private getDataFromStore(): void {
    this.initTableData();
    this.initOrganizations();
  }

  private initOrganizations(): void {
    this.unsubscriber.add(
      this.organizationService.accountState.subscribe(data => {
        const organizations = [
          ...data.map(el => {
            return {
              organizationId: el.organizationId,
              domain: el.domain,
              organizationName: el.contact.organization
            };
          })
        ];
        this.organizationsList = [...organizations];
        this.organizationsFilterList = [
          {
            organizationId: "",
            domain: "Show all PBXs",
            organizationName: ""
          },
          ...organizations
        ];

        if (!(this.form.get("organizationsList")?.value as Array<Pbx>).length) {
          this.form.get("organizationsList")?.patchValue(this.organizationsFilterList[0]);
        }
      })
    );
  }

  private initTableData(): void {
    this.isDataObtained$.next(false);
    this.unsubscriber.add(
      combineLatest({
        dids: this.didService.accountState,
        orgs: this.organizationService.state.pipe(filter(({ loading }) => !loading))
      })
        .pipe(
          filter(({ orgs }) => !!orgs.length),
          map(({ dids, orgs }) => {
            return dids.map(phone => ({
              ...phone,
              domain: orgs.state[phone.organizationId]?.domain,
              control: new FormControl(orgs.state[phone.organizationId]?.domain),
              pbx: orgs.state[phone.organizationId]?.contact?.organization
            }));
          })
        )
        .subscribe(data => {
          this.allPbxes = data;
          this.dataSource.data = data;
          this.isDataObtained$.next(true);
        })
    );
  }

  private onBreakpointChange(): void {
    this.unsubscriber.add(
      this.breakpointObserver.observe(Breakpoints.XSmall).subscribe(result => {
        if (result.matches) {
          this.displayedColumns$.next(["number"]);
        } else {
          this.displayedColumns$.next(["number", "pbx"]);
        }
      })
    );
  }

  private initSorting(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortData = (data, matSort) =>
      data.sort((a, b) => {
        let val = 0;
        if (!matSort.direction) matSort.active = "pbx";
        switch (matSort.active) {
          case "number":
            val = a?.number?.toLowerCase() < b?.number?.toLowerCase() ? -1 : 1;
            break;
          case "pbx":
            val = a?.pbx?.toLowerCase() < b?.pbx?.toLowerCase() ? -1 : 1;

            break;
        }
        return matSort.direction === "desc" ? val * -1 : val;
      });
  }

  setSelectedElements(transferNumber: TransferredDid) {
    let selectedValues = this.form.get("selectedElements")?.value as Array<TransferredDid>;
    const isNewDid = selectedValues.find(el => el.number === transferNumber.number);

    if (isNewDid) {
      selectedValues = selectedValues.filter(el => el.number !== transferNumber.number);
    } else {
      selectedValues = [...selectedValues, transferNumber];
    }

    this.form.get("selectedElements")?.setValue([...selectedValues]);
  }

  onSelectAll(): void {
    this.unsubscriber.add(
      this.form.controls.selectAll.valueChanges.subscribe(isSelectedAll => {
        if (isSelectedAll) {
          const allNumbers = this.dataSource.filteredData;
          this.form.get("selectedElements")?.setValue([...allNumbers]);
        } else {
          this.form.get("selectedElements")?.setValue([]);
        }
      })
    );
  }

  onFilterOrganization(): void {
    const filterValue = this.form.get("organizationsList")?.value as Pbx;
    if (filterValue?.organizationId) {
      this.dataSource.data = [...this.allPbxes].filter(
        el => el.organizationId === filterValue?.organizationId
      );
    } else {
      this.dataSource.data = this.allPbxes;
    }
    this.form.get("selectedElements")?.setValue([]);
  }

  onSearch(): void {
    this.unsubscriber.add(
      this.form.controls.search.valueChanges.subscribe(searchQuery => {
        this.dataSource.filter = searchQuery.trim().toLowerCase();
        const filteredItems = this.form.controls.selectedElements.value.filter(
          (el: TransferredDid) => {
            return this.dataSource.filteredData.includes(el);
          }
        );
        this.form.controls.selectedElements.setValue(filteredItems);
      })
    );
  }

  openTransferDidsModal(): void {
    if (!this.form.controls.selectedElements.value.length) {
      this.snackbarService.openSnackBar(
        "You have to select one or more numbers to transfer",
        "error"
      );
      return;
    }
    const dialog = this.dialog.open(TransferNumbersModalComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      disableClose: true,
      autoFocus: false,
      data: {
        pbxes: this.organizationsList
      }
    });
    let pbx!: Pbx;
    this.unsubscriber.add(
      dialog
        .afterClosed()
        .pipe(
          filter(organization => !!organization),
          switchMap(organization => {
            pbx = organization;
            return this.browseUsers$(organization.domain).pipe(take(1));
          }),
          switchMap(async data => {
            const res = await data;
            const users = Object.values(res.data);
            if (!users.length) {
              throw new Error(`There is no users on that ${pbx.organizationName}`);
            } else {
              const selectedElements: Array<TransferredDid> =
                this.form.controls.selectedElements.value;
              return forkJoin(
                selectedElements.map(phone => {
                  return this.onTransferPhone(pbx, phone, users);
                })
              );
            }
          }),
          switchMap(res => from(res)),
          catchError((err: unknown) => {
            this.snackbarService.openSnackBar(err as string, "error");
            return EMPTY;
          })
        )
        .subscribe((responses: Array<ApiPromiseSubject<Did>>) => {
          if (Array.isArray(responses)) {
            if (responses.every(element => element.status === "success")) {
              this.showSuccessDialog(pbx);
              this.form.get("organizationsList")?.setValue(this.organizationsFilterList[0]);
            } else {
              this.snackbarService.openSnackBar(
                `There are an errors with transferring numbers`,
                "error"
              );
            }
          }
        })
    );
  }

  trackById(index: number, item: TransferredDid): string {
    return item.didId;
  }

  isChecked(transferNumber: string): boolean {
    return !!(this.form.controls.selectedElements.value as Array<TransferredDid>).find(
      el => el.number === transferNumber
    );
  }

  private browseUsers$(domain: string) {
    const org = this.organizationsList.find(org => org.domain === domain);
    return this.accountId$.pipe(
      take(1),
      switchMap(accountId => {
        return of(
          this.userService.userBrowse({
            OrganizationId: org?.organizationId,
            AccountId: accountId,
            Limit: 500
          })
        );
      })
    );
  }

  private setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // number
      const number = this.formatE164.transform(data.number) || "";
      // domain
      const domain = data.domain;

      return (
        number.toLowerCase().includes(filterValue) || domain.toLowerCase().includes(filterValue)
      );
    };
  }
}
